import { PlatformService } from "@propertelligent/client-api";
import { FormProvider, useForm } from "react-hook-form";
import apiHelper from "../../../utils/apiHelper";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import SelectField from "../../../newComponents/form-fields/SelectField";
import DescriptionTextarea from "../../../newComponents/form-fields/DescriptionTextarea";
import Card from "../../../newComponents/card";
import { useEffect, useState, useCallback } from "react";

const FEEDBACK_DESCRIPTION_MAX_LENGTH = 500;

const AddFeedbackForm = ({ isModalOpen, setModalOpen, editData, fetchData }: any) => {
  const [feedbackTypes, setFeedbackTypes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm({
    mode: "onChange",
    defaultValues: editData ? {
      id: editData.id,
      feedbackTypeId: editData.feedbackTypeId,
      feedbackDescription: editData.feedbackDescription,
    } : {
      feedbackTypeId: null, // Initial null taake user select kare
      feedbackDescription: "",
    }
  });

  // Subscribe formState so Save/disabled update when SelectField dirties feedbackTypeId
  // (inline methods.formState.* left the footer stuck until another field re-rendered the parent).
  const {
    formState: { isDirty, isValid, isSubmitting },
  } = methods;

  // API Call to fetch feedback types
  const fetchFeedbackTypes = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await apiHelper(PlatformService.getApiFeedbackFeedbackTypes, {
        showNotification: false,
      });
      if (res?.data?.success) {
        // Map API response to SelectField format (Id, Name)
        const options = res.data.success.map((type: any) => ({
          Id: type.id,
          Name: type.description || type.name,
        }));
        setFeedbackTypes(options);
      }
    } catch (error) {
      console.error("Error fetching feedback types:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      fetchFeedbackTypes();
    }
  }, [isModalOpen, fetchFeedbackTypes]);

  const onSubmit = async (data: any) => {
    const apiCall = editData ? PlatformService.putApiFeedback : PlatformService.postApiFeedback;
    
    const res = await apiHelper(apiCall, { 
      showNotification: true, 
      successMessage: editData ? "Feedback updated successfully" : "Feedback submitted successfully" 
    }, data);
    
    if (res?.data) {
      setModalOpen(false);
      fetchData();
    }
  };

  return (
    <RightSideModal
      isOpen={isModalOpen}
      onClose={() => setModalOpen(false)}
      title={editData ? "Edit Feedback" : "Add Feedback"}
      buttonlabel="Save"
      formId="feedback-form"
      isDirty={isDirty}
      isLoading={isSubmitting}
      isSubmitDisabled={isSubmitting || !isDirty || !isValid}
    >
      <Card className="no-bg">
        <FormProvider {...methods}>
          <form id="feedback-form" onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12">
                <SelectField
                  name="feedbackTypeId"
                  label="Feedback Type"
                  required
                  requiredMessage="Please select a feedback type"
                  options={feedbackTypes}
                  disabled={isLoading}
                  placeholderText={isLoading ? "Loading types..." : "Select Type"}
                />
              </div>
              <div className="col-md-12 mt-3">
                <DescriptionTextarea
                  name="feedbackDescription"
                  label="Description"
                  required
                  requiredMessage="Description is required"
                  rows={5}
                  placeholder="Enter your feedback details here..."
                  validation={{
                    maxLength: {
                      value: FEEDBACK_DESCRIPTION_MAX_LENGTH,
                      message: `Description cannot exceed ${FEEDBACK_DESCRIPTION_MAX_LENGTH} characters`
                    }
                  }}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </Card>
    </RightSideModal>
  );
};

export default AddFeedbackForm;